import { Outlet, useLocation } from "react-router";
import { useMemo } from "react";

import { ITab } from "../../../../../common/Atoms/Tabs";
import { UnderlineTabs } from "../../../../../common/Atoms/UnderlineTabs";
import { Card } from "../../../../../common/Atoms/Card";
import { GroupHeading } from "../GroupHeading";
import { useActions } from "../../../../hooks/useActions";
import { useCurrentGroup } from "../../../../hooks/useGroups";
import { useFeatureFlags } from "../../../../hooks/useFeatureFlags";
import { useAllowedFeature } from "../../../../hooks/useAllowedFeature";
import { ContactDataWarning } from "../../../Molecules/ContactDataWarning";
import { GroupSetupIssues } from "../GroupSetupIssues";

const formatter = new Intl.NumberFormat(`en`, { notation: `compact` });

export function GroupTableSectionRoot() {
  const { data: actions } = useActions();
  const { groupFormData, invalidPolicyCount, paymentPlanConfigs } = useCurrentGroup();
  const validPolicyCount = (groupFormData.policies?.length || 0) - invalidPolicyCount;
  const featureFlags = useFeatureFlags();
  const allowedPaymentPlans = useAllowedFeature(`paymentPlans`);
  const { pathname } = useLocation();

  const tabs: ITab[] = useMemo(() => {
    const tabs: ITab[] = [
      {
        name: `Policies`,
        href: ``,
        badge: {
          text: validPolicyCount.toString(),
          color: `gray`,
        },
        secondaryBadge: invalidPolicyCount > 0 ? {
          text: invalidPolicyCount.toString(),
          className: `text-red-500 bg-red-100`,
          tooltip: `${invalidPolicyCount.toString()} incomplete ${invalidPolicyCount === 1 ? `policy` : `policies`}`,
        } : undefined,
      },
      {
        name: `Contacts`,
        href: `contacts`,
        badge: {
          text: formatter.format(groupFormData.contactsCount || 0),
          color: `gray`,
        },
      },
      {
        name: `Actions`,
        href: `actions`,
        badge: {
          text: formatter.format(actions?.totalPending || 0),
          color: `gray`,
        },
        secondaryBadge: actions?.totalPendingApproval ? {
          text: formatter.format(actions?.totalPendingApproval),
          className: `text-orange-500 bg-orange-100`,
          tooltip: `${formatter.format(actions?.totalPendingApproval)} actions require manual execution.`,
        } : undefined,
      },
      {
        name: `Insights`,
        href: `insights`,
      },
    ];

    if (featureFlags.paymentPlans) {
      const badge = !allowedPaymentPlans ? {
        text: `pro`,
        color: `purple`,
      } : (paymentPlanConfigs?.length || 0) > 0 ? {
        text: `enabled`,
        color: `green`,
      } : undefined;

      tabs.push({
        name: `Payment Plan Settings`,
        href: `paymentplanconfig`,
        ...(badge ? { badge } : {}),
      });
    }

    return tabs;
  }, [validPolicyCount, invalidPolicyCount, groupFormData, actions, allowedPaymentPlans, featureFlags, paymentPlanConfigs]);

  const isPolicyPage = pathname?.includes(`groups/${groupFormData.id}/policies/`);

  if (isPolicyPage) {
    return (
      <div>
        <div className={ `mb-4` }>
          <ContactDataWarning />
        </div>
        <Outlet />
      </div>
    );
  }

  return (
    <Card className={ `mb-[30px]` }>
      <GroupHeading>
        <UnderlineTabs
          tabs={ tabs }
          className={ `mt-3 mb-5 -mx-4 sm:-mx-5 px-4 sm:px-5` }
        >
          <GroupSetupIssues />
        </UnderlineTabs>
      </GroupHeading>
      <Outlet />
    </Card>
  );
}
